import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../../../lib/api';
import { useToastContext } from '../../../contexts/ToastContext';
import { formatDateOnly } from '../../../utils/dateUtils';
import { Spinner } from '../../ui/Spinner';
import {
  Users,
  TrendingUp,
  DollarSign,
  UserPlus,
  CheckCircle,
  CreditCard,
  Plus,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Edit2,
  X,
  Check,
  Filter,
  Activity,
  Link,
  Copy,
  Info,
  Percent,
  ArrowRight,
  Search,
  UserCheck2,
  Loader2,
} from 'lucide-react';

interface DashboardStats {
  total_partners: number;
  active_partners: number;
  total_referrals: number;
  total_signups: number;
  total_onboarded: number;
  total_subscribed: number;
  conversion_rate_signup_to_onboard: number;
  conversion_rate_onboard_to_subscribe: number;
  total_revenue: number;
  total_commission: number;
  recent_signups_7d: number;
  recent_subscriptions_7d: number;
}

interface Partner {
  id: string;
  partner_code: string;
  partner_name: string;
  company_name?: string;
  email: string;
  commission_rate: number;
  status: string;
  tier: string;
  created_at: string;
  total_referrals: number;
  signups: number;
  onboarded: number;
  subscribed: number;
  total_revenue: number;
  total_commission: number;
  user_id?: string;
}

interface UserSearchResult {
  user_id: string;
  email: string;
  full_name?: string;
  created_at?: string;
  already_linked: boolean;
}

interface Referral {
  id: string;
  partner_id: string;
  partner_code: string;
  partner_name: string;
  user_id: string;
  user_email?: string;
  attribution_source: string;
  conversion_status: string;
  signed_up_at: string;
  total_revenue_generated: number;
  total_commission_earned: number;
}

interface AffiliateEvent {
  id: string;
  referral_id: string;
  partner_id: string;
  partner_code?: string;
  user_id?: string;
  user_email?: string;
  event_type: string;
  revenue_amount?: number;
  commission_amount?: number;
  event_data: Record<string, unknown>;
  created_at: string;
}

const ITEMS_PER_PAGE = 10;

export const AffiliateAdminTab: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [referralsTotal, setReferralsTotal] = useState(0);
  const [events, setEvents] = useState<AffiliateEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedPartner, setExpandedPartner] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState<string | null>(null);
  const { showToast } = useToastContext();

  // Filter states
  const [partnerStatusFilter, setPartnerStatusFilter] = useState<string>('all');
  const [referralPartnerFilter, setReferralPartnerFilter] = useState<string>('all');
  const [referralStatusFilter, setReferralStatusFilter] = useState<string>('all');

  // Pagination states
  const [referralsPage, setReferralsPage] = useState(0);
  const [eventsPage, setEventsPage] = useState(0);

  // Form state for creating new partner
  const [newPartner, setNewPartner] = useState({
    partner_code: '',
    partner_name: '',
    company_name: '',
    email: '',
    commission_rate: 10,
    user_id: '' as string | undefined,
  });

  // User search state for linking
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [showUserSearchDropdown, setShowUserSearchDropdown] = useState(false);
  const [linkedUser, setLinkedUser] = useState<{ user_id: string; email: string } | null>(null);
  // User search for expanded partner linking
  const [expandedLinkQuery, setExpandedLinkQuery] = useState('');
  const [expandedLinkResults, setExpandedLinkResults] = useState<UserSearchResult[]>([]);
  const [expandedLinkLoading, setExpandedLinkLoading] = useState(false);
  const [showExpandedLinkDropdown, setShowExpandedLinkDropdown] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    status: 'active',
    commission_rate: 10,
  });

  // Get base URL for referral links
  const baseUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      return `${window.location.protocol}//${window.location.host}`;
    }
    return 'https://app.signaliq.ai';
  }, []);

  // Section-level loading states (avoid full-page spinner on pagination)
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);

  const fetchReferrals = useCallback(async (page: number) => {
    try {
      setReferralsLoading(true);
      const params: {
        partner_id?: string;
        conversion_status?: 'signup' | 'onboarded' | 'subscribed';
        limit: number;
        offset: number;
      } = {
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
      };
      if (referralPartnerFilter !== 'all') {
        params.partner_id = referralPartnerFilter;
      }
      if (referralStatusFilter !== 'all') {
        params.conversion_status = referralStatusFilter as 'signup' | 'onboarded' | 'subscribed';
      }
      const res = await apiClient.listAffiliateReferrals(params);
      setReferrals(res.referrals);
      setReferralsTotal(res.total);
    } catch {
      // handled by API layer
    } finally {
      setReferralsLoading(false);
    }
  }, [referralPartnerFilter, referralStatusFilter]);

  const fetchEvents = useCallback(async (page: number) => {
    try {
      setEventsLoading(true);
      const res = await apiClient.listAffiliateEvents({
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
      });
      setEvents(res.events);
      setEventsTotal(res.total);
    } catch {
      // handled by API layer
    } finally {
      setEventsLoading(false);
    }
  }, []);

  // Full data fetch (initial load + filter changes)
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const partnerParams: { status?: 'active' | 'inactive' | 'suspended'; limit: number } = { limit: 100 };
      if (partnerStatusFilter !== 'all') {
        partnerParams.status = partnerStatusFilter as 'active' | 'inactive' | 'suspended';
      }

      const referralParams: {
        partner_id?: string;
        conversion_status?: 'signup' | 'onboarded' | 'subscribed';
        limit: number;
        offset: number;
      } = {
        limit: ITEMS_PER_PAGE,
        offset: 0,
      };
      if (referralPartnerFilter !== 'all') {
        referralParams.partner_id = referralPartnerFilter;
      }
      if (referralStatusFilter !== 'all') {
        referralParams.conversion_status = referralStatusFilter as 'signup' | 'onboarded' | 'subscribed';
      }

      const [statsRes, partnersRes, referralsRes, eventsRes] = await Promise.all([
        apiClient.getAffiliateDashboardStats(),
        apiClient.listAffiliatePartners(partnerParams),
        apiClient.listAffiliateReferrals(referralParams),
        apiClient.listAffiliateEvents({ limit: ITEMS_PER_PAGE, offset: 0 }),
      ]);
      setStats(statsRes);
      setPartners(partnersRes.partners);
      setReferrals(referralsRes.referrals);
      setReferralsTotal(referralsRes.total);
      setEvents(eventsRes.events);
      setEventsTotal(eventsRes.total);
    } catch {
      showToast('Failed to load affiliate data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, partnerStatusFilter, referralPartnerFilter, referralStatusFilter]);

  // Initial load + filter changes → full fetch (resets to page 0)
  useEffect(() => {
    setReferralsPage(0);
    setEventsPage(0);
    fetchData();
  }, [fetchData]);

  // Referrals pagination → only refetch referrals
  useEffect(() => {
    if (!loading && referralsPage > 0) {
      fetchReferrals(referralsPage);
    }
  }, [referralsPage, fetchReferrals, loading]);

  // Events pagination → only refetch events
  useEffect(() => {
    if (!loading && eventsPage > 0) {
      fetchEvents(eventsPage);
    }
  }, [eventsPage, fetchEvents, loading]);

  // Debounced user search for create form
  useEffect(() => {
    if (userSearchQuery.length < 3) {
      setUserSearchResults([]);
      setShowUserSearchDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setUserSearchLoading(true);
        const result = await apiClient.searchUsersForPartnerLinking(userSearchQuery);
        setUserSearchResults(result.users);
        setShowUserSearchDropdown(true);
      } catch {
        // silent
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  // Debounced user search for expanded partner linking
  useEffect(() => {
    if (expandedLinkQuery.length < 3) {
      setExpandedLinkResults([]);
      setShowExpandedLinkDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setExpandedLinkLoading(true);
        const result = await apiClient.searchUsersForPartnerLinking(expandedLinkQuery);
        setExpandedLinkResults(result.users);
        setShowExpandedLinkDropdown(true);
      } catch {
        // silent
      } finally {
        setExpandedLinkLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [expandedLinkQuery]);

  const handleSelectUser = (user: UserSearchResult) => {
    setLinkedUser({ user_id: user.user_id, email: user.email });
    setNewPartner(prev => ({
      ...prev,
      email: user.email,
      partner_name: user.full_name || prev.partner_name,
      user_id: user.user_id,
    }));
    setUserSearchQuery('');
    setShowUserSearchDropdown(false);
  };

  const handleUnlinkUser = () => {
    setLinkedUser(null);
    setNewPartner(prev => ({ ...prev, user_id: undefined }));
  };

  const handleLinkExistingPartner = async (partnerId: string, user: UserSearchResult) => {
    try {
      await apiClient.updateAffiliatePartner(partnerId, { user_id: user.user_id });
      showToast('User linked to partner', 'success');
      setExpandedLinkQuery('');
      setShowExpandedLinkDropdown(false);
      fetchData();
    } catch {
      showToast('Failed to link user', 'error');
    }
  };

  const handleUnlinkExistingPartner = async (partnerId: string) => {
    try {
      await apiClient.updateAffiliatePartner(partnerId, { user_id: null });
      showToast('User unlinked from partner', 'success');
      fetchData();
    } catch {
      showToast('Failed to unlink user', 'error');
    }
  };

  const handleCreatePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.createAffiliatePartner({
        partner_code: newPartner.partner_code.toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
        partner_name: newPartner.partner_name,
        company_name: newPartner.company_name || undefined,
        email: newPartner.email,
        commission_rate: newPartner.commission_rate,
        user_id: newPartner.user_id || undefined,
      });
      showToast('Partner created successfully', 'success');
      setShowCreateForm(false);
      setLinkedUser(null);
      setNewPartner({
        partner_code: '',
        partner_name: '',
        company_name: '',
        email: '',
        commission_rate: 10,
        user_id: undefined,
      });
      fetchData();
    } catch {
      showToast('Failed to create partner', 'error');
    }
  };

  const handleUpdatePartner = async (partnerId: string) => {
    try {
      await apiClient.updateAffiliatePartner(partnerId, {
        status: editForm.status as 'active' | 'inactive' | 'suspended',
        commission_rate: editForm.commission_rate,
      });
      showToast('Partner updated successfully', 'success');
      setEditingPartner(null);
      fetchData();
    } catch {
      showToast('Failed to update partner', 'error');
    }
  };

  const startEditing = (partner: Partner) => {
    setEditingPartner(partner.id);
    setEditForm({
      status: partner.status,
      commission_rate: partner.commission_rate,
    });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Copied to clipboard', 'success');
    } catch {
      showToast('Failed to copy', 'error');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => formatDateOnly(dateString);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      case 'suspended':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getConversionStatusColor = (status: string) => {
    switch (status) {
      case 'signup':
        return 'bg-blue-100 text-blue-700';
      case 'onboarded':
        return 'bg-amber-100 text-amber-700';
      case 'subscribed':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case 'signup':
        return 'bg-blue-100 text-blue-700';
      case 'onboarding_complete':
        return 'bg-amber-100 text-amber-700';
      case 'first_subscription':
        return 'bg-green-100 text-green-700';
      case 'subscription_payment':
      case 'subscription_renewal':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatEventType = (eventType: string) => {
    return eventType.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Pagination helpers
  const referralsTotalPages = Math.ceil(referralsTotal / ITEMS_PER_PAGE);
  const eventsTotalPages = Math.ceil(eventsTotal / ITEMS_PER_PAGE);

  if (loading) {
    return <Spinner size="lg" text="Loading affiliate data..." center />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
          <Users className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Affiliate Program</h2>
          <p className="text-gray-600 text-sm">Manage partners, track referrals, and monitor commissions</p>
        </div>
      </div>

      {/* How It Works Info Card */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-5">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Info className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-2">How Affiliate Attribution Works</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
                <div>
                  <p className="font-medium text-gray-800">Referral Link</p>
                  <p className="text-gray-600">Users visit with <code className="bg-white px-1.5 py-0.5 rounded text-xs">?ref=CODE</code></p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
                <div>
                  <p className="font-medium text-gray-800">Conversion Tracking</p>
                  <p className="text-gray-600">Signup → Onboarding complete → Subscribe</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
                <div>
                  <p className="font-medium text-gray-800">Commission</p>
                  <p className="text-gray-600">Partner earns % of each subscription payment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-sm border border-blue-100 p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-700">Active Partners</span>
            <Users className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-blue-600">{stats?.active_partners || 0}</div>
          <div className="text-xs text-gray-500 mt-1">{stats?.total_partners || 0} total</div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-white rounded-xl shadow-sm border border-green-100 p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-700">Total Referrals</span>
            <UserPlus className="w-5 h-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-green-600">{stats?.total_referrals || 0}</div>
          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            {stats?.recent_signups_7d || 0} in last 7 days
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl shadow-sm border border-purple-100 p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-purple-700">Subscribed</span>
            <CreditCard className="w-5 h-5 text-purple-500" />
          </div>
          <div className="text-3xl font-bold text-purple-600">{stats?.total_subscribed || 0}</div>
          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            {stats?.recent_subscriptions_7d || 0} in last 7 days
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl shadow-sm border border-amber-100 p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-amber-700">Total Revenue</span>
            <DollarSign className="w-5 h-5 text-amber-500" />
          </div>
          <div className="text-3xl font-bold text-amber-600">{formatCurrency(stats?.total_revenue || 0)}</div>
          <div className="text-xs text-gray-500 mt-1">
            {formatCurrency(stats?.total_commission || 0)} commission paid
          </div>
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Funnel</h3>
        <div className="flex items-center justify-center gap-2 md:gap-4">
          <div className="text-center flex-1">
            <div className="text-3xl font-bold text-blue-600">{stats?.total_signups || 0}</div>
            <div className="text-sm text-gray-600 font-medium">Signups</div>
          </div>
          <div className="flex flex-col items-center text-gray-400">
            <ArrowRight className="w-5 h-5" />
            <span className="text-xs mt-1">{stats?.conversion_rate_signup_to_onboard || 0}%</span>
          </div>
          <div className="text-center flex-1">
            <div className="text-3xl font-bold text-amber-600">{stats?.total_onboarded || 0}</div>
            <div className="text-sm text-gray-600 font-medium">Onboarded</div>
            <div className="text-xs text-gray-400 mt-0.5">Completed onboarding flow</div>
          </div>
          <div className="flex flex-col items-center text-gray-400">
            <ArrowRight className="w-5 h-5" />
            <span className="text-xs mt-1">{stats?.conversion_rate_onboard_to_subscribe || 0}%</span>
          </div>
          <div className="text-center flex-1">
            <div className="text-3xl font-bold text-green-600">{stats?.total_subscribed || 0}</div>
            <div className="text-sm text-gray-600 font-medium">Subscribed</div>
            <div className="text-xs text-gray-400 mt-0.5">Made first payment</div>
          </div>
        </div>
      </div>

      {/* Partners Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-visible">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-gray-900">Partners</h3>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={partnerStatusFilter}
                onChange={(e) => setPartnerStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Partner
          </button>
        </div>

        {/* Create Partner Form */}
        {showCreateForm && (
          <div className="p-4 bg-blue-50 border-b border-blue-100">
            <form onSubmit={handleCreatePartner} className="space-y-4">
              {/* Link to User (optional) */}
              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Link to User (optional)
                </label>
                {linkedUser ? (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-green-200 rounded-lg text-sm">
                      <UserCheck2 className="w-4 h-4 text-green-600" />
                      <span className="text-gray-700">Linked: {linkedUser.email}</span>
                    </div>
                    <button
                      type="button"
                      onClick={handleUnlinkUser}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      aria-label="Unlink user"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by email to link a user..."
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {userSearchLoading && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                      )}
                    </div>
                    {showUserSearchDropdown && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {userSearchResults.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
                        ) : (
                          userSearchResults.map((u) => (
                            <button
                              key={u.user_id}
                              type="button"
                              disabled={u.already_linked}
                              onClick={() => handleSelectUser(u)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-between"
                            >
                              <div>
                                <span className="font-medium text-gray-900">{u.email}</span>
                                {u.full_name && (
                                  <span className="text-gray-500 ml-2">{u.full_name}</span>
                                )}
                              </div>
                              {u.already_linked && (
                                <span className="text-xs text-amber-600 font-medium">(already a partner)</span>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Partner Code</label>
                  <input
                    type="text"
                    placeholder="e.g., ACME_2024"
                    value={newPartner.partner_code}
                    onChange={(e) => setNewPartner({ ...newPartner, partner_code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Partner Name</label>
                  <input
                    type="text"
                    placeholder="Company or person name"
                    value={newPartner.partner_name}
                    onChange={(e) => setNewPartner({ ...newPartner, partner_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    placeholder="contact@example.com"
                    value={newPartner.email}
                    onChange={(e) => setNewPartner({ ...newPartner, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Commission %</label>
                  <input
                    type="number"
                    value={newPartner.commission_rate}
                    onChange={(e) => setNewPartner({ ...newPartner, commission_rate: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    max="100"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                >
                  Create Partner
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Partners List */}
        <div className="divide-y divide-gray-100">
          {partners.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No partners yet</p>
              <p className="text-gray-400 text-sm">Create your first partner to get started</p>
            </div>
          ) : (
            partners.map((partner) => (
              <div key={partner.id} className="hover:bg-gray-50 transition-colors">
                <div
                  className="p-4 flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedPartner(expandedPartner === partner.id ? null : partner.id)}
                >
                  <div className="flex items-center gap-4">
                    {expandedPartner === partner.id ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{partner.partner_name}</span>
                        <code className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600 font-mono">
                          {partner.partner_code}
                        </code>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(partner.status)}`}>
                          {partner.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">{partner.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-8 text-sm">
                    <div className="text-center">
                      <div className="font-semibold text-gray-900">{partner.total_referrals}</div>
                      <div className="text-gray-500 text-xs">Referrals</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-gray-900">{partner.subscribed}</div>
                      <div className="text-gray-500 text-xs">Subscribed</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-gray-900">{formatCurrency(partner.total_revenue)}</div>
                      <div className="text-gray-500 text-xs">Revenue</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-emerald-600">{partner.commission_rate}%</div>
                      <div className="text-gray-500 text-xs">Commission</div>
                    </div>
                  </div>
                </div>

                {/* Expanded Partner Details */}
                {expandedPartner === partner.id && (
                  <div className="px-4 pb-4 ml-9 space-y-4">
                    {/* Referral Link */}
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Link className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-800">Referral Link</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white px-3 py-2 rounded border border-emerald-200 text-sm font-mono text-gray-700 truncate">
                          {baseUrl}/?ref={partner.partner_code}
                        </code>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(`${baseUrl}/?ref=${partner.partner_code}`);
                          }}
                          className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                          title="Copy link"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-emerald-700 mt-2">
                        Share this link with the partner. Attribution is automatic when users sign up through this URL.
                      </p>
                    </div>

                    {/* Edit Form or Details */}
                    {editingPartner === partner.id ? (
                      <div className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                          <select
                            value={editForm.status}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="suspended">Suspended</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Commission %</label>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={editForm.commission_rate}
                              onChange={(e) => setEditForm({ ...editForm, commission_rate: Number(e.target.value) })}
                              className="w-20 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                              min="0"
                              max="100"
                            />
                            <Percent className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                        <div className="flex items-end gap-2 ml-auto">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdatePartner(partner.id);
                            }}
                            className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingPartner(null);
                            }}
                            className="p-2 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-4 gap-8 text-sm">
                            <div>
                              <span className="text-gray-500">Created:</span>{' '}
                              <span className="font-medium">{formatDate(partner.created_at)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Tier:</span>{' '}
                              <span className="font-medium capitalize">{partner.tier}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Onboarded:</span>{' '}
                              <span className="font-medium">{partner.onboarded}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Commission Earned:</span>{' '}
                              <span className="font-medium text-emerald-600">{formatCurrency(partner.total_commission)}</span>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditing(partner);
                            }}
                            className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* User Linking */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          {partner.user_id ? (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-sm">
                                <UserCheck2 className="w-4 h-4 text-green-600" />
                                <span className="text-gray-600">Linked to user account</span>
                                <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-500 font-mono">
                                  {partner.user_id.slice(0, 8)}...
                                </code>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnlinkExistingPartner(partner.id);
                                }}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <X className="w-3 h-3" />
                                Unlink
                              </button>
                            </div>
                          ) : (
                            <div className="relative">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-medium text-gray-600">Link to User Account</span>
                              </div>
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                  type="text"
                                  placeholder="Search by email..."
                                  value={expandedPartner === partner.id ? expandedLinkQuery : ''}
                                  onChange={(e) => {
                                    setExpandedLinkQuery(e.target.value);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                {expandedLinkLoading && (
                                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                                )}
                              </div>
                              {showExpandedLinkDropdown && expandedPartner === partner.id && (
                                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                  {expandedLinkResults.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
                                  ) : (
                                    expandedLinkResults.map((u) => (
                                      <button
                                        key={u.user_id}
                                        type="button"
                                        disabled={u.already_linked}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleLinkExistingPartner(partner.id, u);
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-between"
                                      >
                                        <div>
                                          <span className="font-medium text-gray-900">{u.email}</span>
                                          {u.full_name && (
                                            <span className="text-gray-500 ml-2">{u.full_name}</span>
                                          )}
                                        </div>
                                        {u.already_linked && (
                                          <span className="text-xs text-amber-600 font-medium">(already a partner)</span>
                                        )}
                                      </button>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Referrals Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Referrals</h3>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={referralPartnerFilter}
                  onChange={(e) => setReferralPartnerFilter(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Partners</option>
                  {partners.map((p) => (
                    <option key={p.id} value={p.id}>{p.partner_name}</option>
                  ))}
                </select>
              </div>
              <select
                value={referralStatusFilter}
                onChange={(e) => setReferralStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="signup">Signup</option>
                <option value="onboarded">Onboarded</option>
                <option value="subscribed">Subscribed</option>
              </select>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Partner</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">User</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Source</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Signed Up</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {referralsLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : referrals.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    No referrals found
                  </td>
                </tr>
              ) : (
                referrals.map((referral) => (
                  <tr key={referral.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{referral.partner_name}</div>
                      <code className="text-xs text-gray-500">{referral.partner_code}</code>
                    </td>
                    <td className="px-4 py-3">
                      {referral.user_email ? (
                        <span className="text-gray-700">{referral.user_email}</span>
                      ) : null}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(referral.user_id);
                        }}
                        className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono hover:bg-gray-200 hover:text-gray-800 transition-colors cursor-pointer"
                        title={`Click to copy: ${referral.user_id}`}
                      >
                        {referral.user_id.slice(0, 8)}...
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        referral.attribution_source === 'url' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {referral.attribution_source === 'url' ? 'URL' : 'Manual'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getConversionStatusColor(referral.conversion_status)}`}>
                        {referral.conversion_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDate(referral.signed_up_at)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {formatCurrency(referral.total_revenue_generated)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {referralsTotalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Showing {referralsPage * ITEMS_PER_PAGE + 1} - {Math.min((referralsPage + 1) * ITEMS_PER_PAGE, referralsTotal)} of {referralsTotal}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setReferralsPage(p => Math.max(0, p - 1))}
                disabled={referralsPage === 0}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">
                Page {referralsPage + 1} of {referralsTotalPages}
              </span>
              <button
                onClick={() => setReferralsPage(p => Math.min(referralsTotalPages - 1, p + 1))}
                disabled={referralsPage >= referralsTotalPages - 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Events Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Recent Events</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Event</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Partner</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">User</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Revenue</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Commission</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {eventsLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    No events yet
                  </td>
                </tr>
              ) : (
                events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getEventTypeColor(event.event_type)}`}>
                        {formatEventType(event.event_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                        {event.partner_code || 'N/A'}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      {event.user_email ? (
                        <span className="text-gray-700 text-xs">{event.user_email}</span>
                      ) : null}
                      {event.user_id ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(event.user_id!);
                          }}
                          className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono hover:bg-gray-200 hover:text-gray-800 transition-colors cursor-pointer"
                          title={`Click to copy: ${event.user_id}`}
                        >
                          {event.user_id.slice(0, 8)}...
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {event.revenue_amount != null ? formatCurrency(event.revenue_amount) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-600">
                      {event.commission_amount != null ? formatCurrency(event.commission_amount) : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDateTime(event.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {eventsTotalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Showing {eventsPage * ITEMS_PER_PAGE + 1} - {Math.min((eventsPage + 1) * ITEMS_PER_PAGE, eventsTotal)} of {eventsTotal}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEventsPage(p => Math.max(0, p - 1))}
                disabled={eventsPage === 0}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">
                Page {eventsPage + 1} of {eventsTotalPages}
              </span>
              <button
                onClick={() => setEventsPage(p => Math.min(eventsTotalPages - 1, p + 1))}
                disabled={eventsPage >= eventsTotalPages - 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateAdminTab;
